
export const RIDDLE_ROSTER = [
  { id: 'star',     label: 'Star',     emoji: '⭐', color: '#FFD930' },
  { id: 'mushroom', label: 'Mushroom', emoji: '🍄', color: '#FF69B4' },
  { id: 'alien',    label: 'Alien',    emoji: '👽', color: '#39FF14' },
  { id: 'ghost',    label: 'Ghost',    emoji: '👻', color: '#C0C0C0' },
  { id: 'planet',   label: 'Planet',   emoji: '🪐', color: '#18B8E8' },
  { id: 'robot',    label: 'Robot',    emoji: '🤖', color: '#FF7A1A' },
  { id: 'dragon',   label: 'Dragon',   emoji: '🐉', color: '#9B4EC4' },
  { id: 'comet',    label: 'Comet',    emoji: '☄️',  color: '#1AD9C4' },
];


const SCENARIOS = {


  5: {
    characters: ['star', 'ghost', 'planet', 'mushroom', 'alien'],
    correctOrder: ['star', 'ghost', 'planet', 'mushroom', 'alien'],
    intro: 'Five beings — Star, Ghost, Planet, Mushroom, and Alien — are racing to finish the cosmic feast!',
    clues: [
      'Ghost does not finish first or last.',
      'Planet finishes after Ghost.',
      'Mushroom finishes before Alien.',
      'Star finishes before everyone else.',
      'Alien finishes last.',
      'Mushroom does not finish third.',
      'Planet finishes in the middle.',
      'Ghost finishes second.',
      'The top two finishers are Star and Ghost.',
      'Mushroom finishes in 4th place.',
      'Neither Ghost nor Planet finishes last.',
      'Star, Ghost, and Planet finish before Mushroom.',
    ],
    redHerringClues: [
      'Ghost once trained with Alien — but that was long ago.',
      'Planet arrived late to the starting line.',
      'Mushroom brought snacks for everyone.',
    ],
  },


  6: {
    characters: ['star', 'ghost', 'planet', 'mushroom', 'alien', 'robot'],
    correctOrder: ['star', 'ghost', 'robot', 'planet', 'mushroom', 'alien'],
    intro: 'Six contestants enter the Neon Arena. Only one can cross the finish line first!',
    clues: [
      'Star finishes before all others.',
      'Alien finishes last.',
      'Ghost finishes before Robot.',
      'Robot finishes before Planet.',
      'Planet finishes before Mushroom.',
      'Mushroom does not finish last.',
      'Ghost finishes second overall.',
      'Robot finishes in the top half.',
      'Neither Planet nor Mushroom finishes in the top two.',
      'The last two finishers are Mushroom and Alien, in that order.',
      'Ghost, Robot, and Planet occupy positions 2, 3, and 4.',
      'Star is the only one to finish ahead of Ghost.',
    ],
    redHerringClues: [
      'Robot has the fastest warm-up time — but warm-ups don\'t count.',
      'Planet once beat Alien in a practice run.',
      'Mushroom prefers to run in last place to enjoy the scenery.',
    ],
  },


  7: {
    characters: ['star', 'ghost', 'planet', 'mushroom', 'alien', 'robot', 'dragon'],
    correctOrder: ['star', 'ghost', 'robot', 'dragon', 'planet', 'mushroom', 'alien'],
    intro: 'Seven cosmic racers line up on the Prism Speedway. The finish line only has one champion!',
    clues: [
      'Star finishes first.',
      'Alien finishes last.',
      'Ghost finishes second.',
      'Robot finishes before Dragon.',
      'Dragon finishes before Planet.',
      'Planet finishes before Mushroom.',
      'Mushroom finishes before Alien.',
      'Robot occupies position 3.',
      'The middle position (4th) belongs to Dragon.',
      'Planet, Mushroom, and Alien occupy the last three positions.',
      'Ghost finishes immediately after Star.',
      'No one finishes between Robot and Ghost.',
    ],
    redHerringClues: [
      'Dragon breathed fire at the starting pistol — but the race still counted.',
      'Robot and Planet briefly swapped lanes mid-race.',
      'Mushroom was disqualified in a previous tournament — but not this one.',
    ],
  },


  8: {
    characters: ['star', 'ghost', 'planet', 'mushroom', 'alien', 'robot', 'dragon', 'comet'],
    correctOrder: ['comet', 'star', 'ghost', 'robot', 'dragon', 'planet', 'mushroom', 'alien'],
    intro: 'Eight legendary racers compete in the Grand Prism Championship. The cosmos holds its breath!',
    clues: [
      'Comet finishes first — nothing moves faster.',
      'Alien finishes last.',
      'Star finishes second.',
      'Ghost finishes third.',
      'Robot finishes before Dragon.',
      'Dragon finishes before Planet.',
      'Planet finishes before Mushroom.',
      'Mushroom finishes before Alien.',
      'Robot occupies position 4.',
      'Dragon is exactly in the middle (position 5 of 8).',
      'The top three are Comet, Star, and Ghost.',
      'The bottom three are Planet, Mushroom, and Alien.',
      'Comet, Star, and Ghost finish before Robot.',
      'No one finishes between Ghost and Robot.',
    ],
    redHerringClues: [
      'Comet nearly crashed into Star at the first corner.',
      'Dragon and Robot trained together — but finished apart.',
      'Mushroom was leading at the halfway mark.',
      'Alien complained about the track conditions.',
    ],
  },
};



/**
 * Returns everything the RiddleGame needs for a given character count.
 * Falls back to 5-character scenario if count is out of range.
 *
 * @param {number} characterCount  — from config.difficulty.characterCount (5–8)
 * @returns {{ characters, correctOrder, intro, clues, redHerringClues }}
 */
export function getRiddleScenario(characterCount) {
  const count   = Math.min(Math.max(characterCount, 5), 8);
  const scenario = SCENARIOS[count];
  // Return hydrated characters (full objects from RIDDLE_ROSTER)
  const characters = scenario.characters.map(id =>
    RIDDLE_ROSTER.find(r => r.id === id)
  );
  return {
    characters,
    correctOrder:     scenario.correctOrder,
    intro:            scenario.intro,
    clues:            scenario.clues,
    redHerringClues:  scenario.redHerringClues,
  };
}

/**
 * Generates a random number map for a set of character IDs.
 * Each character receives a unique integer from 1 to N.
 * @param {string[]} characterIds
 * @returns {{ [id]: number }}
 */
export function generateNumberMap(characterIds) {
  const n       = characterIds.length;
  const numbers = shuffle(Array.from({ length: n }, (_, i) => i + 1));
  const map     = {};
  characterIds.forEach((id, i) => { map[id] = numbers[i]; });
  return map;
}


export function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}